import { deleteCookie, setCookie } from "@/lib/cookie";

export const DEFAULT_LOCALE = "en-US";
export const LOCALE_COOKIE_NAME = "oai-locale";
export const LOCALE_SEARCH_PARAM = "locale";

export const AVAILABLE_LOCALES = [
  "am",
  "ar",
  "bg-BG",
  "bn-BD",
  "bs-BA",
  "ca-ES",
  "cs-CZ",
  "da-DK",
  "de-DE",
  "el-GR",
  "en-US",
  "es-419",
  "es-ES",
  "et-EE",
  "fa",
  "fi-FI",
  "fr-CA",
  "fr-FR",
  "gu-IN",
  "hi-IN",
  "hr-HR",
  "hu-HU",
  "hy-AM",
  "id-ID",
  "is-IS",
  "it-IT",
  "ja-JP",
  "ka-GE",
  "kk",
  "kn-IN",
  "ko-KR",
  "lt",
  "lv-LV",
  "mk-MK",
  "ml",
  "mn",
  "mr-IN",
  "ms-MY",
  "my-MM",
  "nb-NO",
  "nl-NL",
  "pa",
  "pl-PL",
  "pt-BR",
  "pt-PT",
  "ro-RO",
  "ru-RU",
  "sk-SK",
  "sl-SI",
  "so-SO",
  "sq-AL",
  "sr-RS",
  "sv-SE",
  "sw-TZ",
  "ta-IN",
  "te-IN",
  "th-TH",
  "tl",
  "tr-TR",
  "uk-UA",
  "ur",
  "vi-VN",
  "zh-CN",
  "zh-HK",
  "zh-TW",
] as const;

export type LocaleKey = (typeof AVAILABLE_LOCALES)[number];
export type Messages = Record<string, string>;
export type LocaleDirection = "ltr" | "rtl";

export const EMPTY_LOCALE_MESSAGES: Messages = {};

const LANGUAGE_LOCALE_MAP = {
  am: "am",
  ar: "ar",
  bg: "bg-BG",
  bn: "bn-BD",
  bs: "bs-BA",
  ca: "ca-ES",
  cs: "cs-CZ",
  da: "da-DK",
  de: "de-DE",
  el: "el-GR",
  en: "en-US",
  es: "es-ES",
  et: "et-EE",
  fa: "fa",
  fi: "fi-FI",
  fil: "tl",
  fr: "fr-FR",
  gu: "gu-IN",
  hi: "hi-IN",
  hr: "hr-HR",
  hu: "hu-HU",
  hy: "hy-AM",
  id: "id-ID",
  is: "is-IS",
  it: "it-IT",
  ja: "ja-JP",
  ka: "ka-GE",
  kk: "kk",
  kn: "kn-IN",
  ko: "ko-KR",
  lt: "lt",
  lv: "lv-LV",
  mk: "mk-MK",
  ml: "ml",
  mn: "mn",
  mr: "mr-IN",
  ms: "ms-MY",
  my: "my-MM",
  nb: "nb-NO",
  nl: "nl-NL",
  pa: "pa",
  pl: "pl-PL",
  pt: "pt-BR",
  ro: "ro-RO",
  ru: "ru-RU",
  sk: "sk-SK",
  sl: "sl-SI",
  so: "so-SO",
  sq: "sq-AL",
  sr: "sr-RS",
  sv: "sv-SE",
  sw: "sw-TZ",
  ta: "ta-IN",
  te: "te-IN",
  th: "th-TH",
  tl: "tl",
  tr: "tr-TR",
  uk: "uk-UA",
  ur: "ur",
  vi: "vi-VN",
  zh: "zh-CN",
} satisfies Record<string, LocaleKey>;

const LOCALE_ALIASES = {
  "es-ar": "es-419",
  "es-bo": "es-419",
  "es-br": "es-419",
  "es-bz": "es-419",
  "es-cl": "es-419",
  "es-co": "es-419",
  "es-cr": "es-419",
  "es-cu": "es-419",
  "es-do": "es-419",
  "es-ec": "es-419",
  "es-gt": "es-419",
  "es-hn": "es-419",
  "es-jp": "es-419",
  "es-mx": "es-419",
  "es-ni": "es-419",
  "es-pa": "es-419",
  "es-pe": "es-419",
  "es-pr": "es-419",
  "es-py": "es-419",
  "es-sv": "es-419",
  "es-us": "es-419",
  "es-uy": "es-419",
  "es-ve": "es-419",
  "pt-ao": "pt-PT",
  "pt-ch": "pt-PT",
  "pt-cv": "pt-PT",
  "pt-fr": "pt-PT",
  "pt-gq": "pt-PT",
  "pt-gw": "pt-PT",
  "pt-lu": "pt-PT",
  "pt-mo": "pt-PT",
  "pt-mz": "pt-PT",
  "pt-st": "pt-PT",
  "pt-tl": "pt-PT",
  "fil-ph": "tl",
  "zh-hans": "zh-CN",
  "zh-hans-cn": "zh-CN",
  "zh-hant": "zh-TW",
  "zh-hant-tw": "zh-TW",
  "zh-hant-hk": "zh-HK",
} satisfies Record<string, LocaleKey>;

const LOCALE_MAP = {
  ...Object.fromEntries(AVAILABLE_LOCALES.map((locale) => [locale.toLowerCase(), locale])),
  ...LANGUAGE_LOCALE_MAP,
  ...LOCALE_ALIASES,
} as Record<string, LocaleKey>;

const RTL_LANGUAGES = new Set(["ar", "fa", "ur"]);

export function sanitizeLocale(unsanitizedLocale: string): LocaleKey | null {
  const fullCandidate = unsanitizedLocale.trim().replaceAll("_", "-").toLowerCase();
  if (!fullCandidate) {
    return null;
  }

  const directMatch = LOCALE_MAP[fullCandidate];
  if (directMatch != null) {
    return directMatch;
  }

  const segmentList = fullCandidate.split("-");
  for (let end = segmentList.length - 1; end >= 1; end--) {
    const partialCandidate = segmentList.slice(0, end).join("-");
    const partialMatch = LOCALE_MAP[partialCandidate];
    if (partialMatch != null) {
      return partialMatch;
    }
  }

  return null;
}

export function getLocaleFromLandingPathname(pathname: string): LocaleKey | null {
  const match = /^\/([^/]+)\/?$/.exec(pathname);
  const localeSegment = match?.[1];
  if (localeSegment == null) {
    return null;
  }

  try {
    return sanitizeLocale(decodeURIComponent(localeSegment));
  } catch {
    return null;
  }
}

export function getLocalizedLandingPath(locale: LocaleKey): string {
  return locale === DEFAULT_LOCALE ? "/" : `/${locale}`;
}

export function getLocaleDirection(locale: LocaleKey): LocaleDirection {
  return RTL_LANGUAGES.has(locale.split("-")[0] ?? "") ? "rtl" : "ltr";
}

export function getLocaleDisplayName(locale: LocaleKey): string {
  try {
    return new Intl.DisplayNames([locale], { type: "language" }).of(locale) ?? locale;
  } catch {
    return locale;
  }
}

export function setLocalePreferenceCookie(locale: LocaleKey | null) {
  if (locale == null) {
    deleteCookie(LOCALE_COOKIE_NAME);
    return;
  }

  setCookie(LOCALE_COOKIE_NAME, locale);
}

export async function importLocaleJson(locale: LocaleKey): Promise<Messages> {
  if (locale === DEFAULT_LOCALE) {
    return EMPTY_LOCALE_MESSAGES;
  }

  try {
    const { importLocaleJsonFromLoader } = await import("@/locale-loader");
    return await importLocaleJsonFromLoader(locale);
  } catch (error) {
    console.error(`Failed to import locale json for ${locale}`, error);
    return EMPTY_LOCALE_MESSAGES;
  }
}
